import React from 'react';
import SearchableSelect from '../ui/SearchableSelect';
import { DayWiseDetail, BasicInfo, Accommodation, MasterData } from '../../types';

interface Props {
  dayWiseDetails: DayWiseDetail[];
  basicInfo: BasicInfo;
  accommodations: Accommodation[];
  masterData: MasterData;
  onChange: (dayWiseDetails: DayWiseDetail[]) => void;
}

const DayWiseForm: React.FC<Props> = ({ dayWiseDetails, basicInfo, accommodations, masterData, onChange }) => {
  const getMainActivityOptions = (day: DayWiseDetail): { id: number; name: string }[] => {
    let options: { id: number; name: string }[] = [];
    if (day.is_custom_tour) {
      options = (day.activities || []).filter(a => a.activity_id !== -1 && a.activity_name).map(a => ({ id: a.activity_id, name: a.activity_name }));
    } else if (day.tour_id !== -1 && masterData.tours_activity_ids?.[day.tour_id]) {
      const activityIds = masterData.tours_activity_ids[day.tour_id];
      const names = masterData.activities_names || {};
      options = activityIds
        .filter((id: number) => names[id])
        .map((id: number) => ({ id, name: names[id] }));
    } else if (day.tour_id !== -1 && day.tour_name) {
      options = [{ id: day.tour_id, name: day.tour_name }];
    }
    // Add accommodation option
    if (accommodations.length > 0) {
      options.push({ id: -99, name: 'Accommodation 🏨' });
    }
    return options;
  };

  const updateDay = (index: number, updates: Partial<DayWiseDetail>) => {
    const updated = [...dayWiseDetails];
    updated[index] = { ...updated[index], ...updates };
    console.log(`Day ${index + 1} updated:`, updates);
    console.log(`Full day data:`, updated[index]);
    onChange(updated);
  };

  const addDayAddon = (dayIndex: number) => {
    const updated = [...dayWiseDetails];
    updated[dayIndex].day_addons.push({ activity_id: -1, activity_name: '', calculated_adults: 0 });
    onChange(updated);
  };

  const removeDayAddon = (dayIndex: number) => {
    const updated = [...dayWiseDetails];
    if (updated[dayIndex].day_addons.length > 0) {
      updated[dayIndex].day_addons.pop();
    }
    onChange(updated);
  };

  const updateDayAddon = (dayIndex: number, addonIndex: number, field: string, value: any) => {
    const updated = [...dayWiseDetails];
    updated[dayIndex].day_addons[addonIndex] = {
      ...updated[dayIndex].day_addons[addonIndex],
      [field]: value
    };
    onChange(updated);
  };

  const addDayExtra = (dayIndex: number) => {
    const updated = [...dayWiseDetails];
    updated[dayIndex].day_extras.push({ extra_text: '', rate: 0, number: 0, display_in_itinerary: true });
    onChange(updated);
  };

  const removeDayExtra = (dayIndex: number) => {
    const updated = [...dayWiseDetails];
    if (updated[dayIndex].day_extras.length > 0) {
      updated[dayIndex].day_extras.pop();
    }
    onChange(updated);
  };

  const updateDayExtra = (dayIndex: number, extraIndex: number, field: string, value: any) => {
    const updated = [...dayWiseDetails];
    updated[dayIndex].day_extras[extraIndex] = {
      ...updated[dayIndex].day_extras[extraIndex],
      [field]: value
    };
    onChange(updated);
  };

  const moveDay = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index > 0) {
      const updated = [...dayWiseDetails];
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      // Update day numbers
      updated[index - 1].day = index;
      updated[index].day = index + 1;
      onChange(updated);
    } else if (direction === 'down' && index < dayWiseDetails.length - 1) {
      const updated = [...dayWiseDetails];
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      // Update day numbers
      updated[index].day = index + 1;
      updated[index + 1].day = index + 2;
      onChange(updated);
    }
  };

  return (
    <div className="space-y-6">
      {dayWiseDetails.map((day, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-6 bg-gradient-to-br from-white to-gray-50">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-gray-900">
              📅 Day {day.day} - {(() => {
                if (!day.tour_date || day.tour_date === '') return 'No Date';
                try {
                  const date = new Date(day.tour_date);
                  if (isNaN(date.getTime())) return 'Invalid Date';
                  const dd = String(date.getDate()).padStart(2, '0');
                  const mm = String(date.getMonth() + 1).padStart(2, '0');
                  const yyyy = date.getFullYear();
                  return `${dd}/${mm}/${yyyy}`;
                } catch {
                  return 'Invalid Date';
                }
              })()}
            </h3>
            <div className="flex gap-2">
              {index > 0 && (
                <button
                  onClick={() => moveDay(index, 'up')}
                  className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                >
                  ⬆️
                </button>
              )}
              {index < dayWiseDetails.length - 1 && (
                <button
                  onClick={() => moveDay(index, 'down')}
                  className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                >
                  ⬇️
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={day.lunch_required}
                onChange={(e) => {
                  const updates: any = { lunch_required: e.target.checked };
                  if (!e.target.checked) {
                    updates.lunch = { meal_id: -1, meal_name: '', foc: 0 };
                  }
                  updateDay(index, updates);
                }}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">Lunch Required</label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={day.dinner_required}
                onChange={(e) => {
                  const updates: any = { dinner_required: e.target.checked };
                  if (!e.target.checked) {
                    updates.dinner = { meal_id: -1, meal_name: '', foc: 0 };
                  }
                  updateDay(index, updates);
                }}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">Dinner Required</label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={day.is_custom_tour}
                onChange={(e) => updateDay(index, { is_custom_tour: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">Custom Tour</label>
            </div>
          </div>

          {day.is_custom_tour ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Custom Tour Name</label>
                  <input
                    type="text"
                    value={day.tour_name}
                    onChange={(e) => updateDay(index, { tour_name: e.target.value })}
                    className="input-field"
                    placeholder="Enter tour name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trip Type</label>
                  <select
                    value={day.custom_trip_type_id || -1}
                    onChange={(e) => {
                      const selectedId = parseInt(e.target.value);
                      if (selectedId === -1) {
                        updateDay(index, { custom_trip_type_id: -1, custom_trip_type: '' });
                      } else {
                        // Find the name for the selected ID
                        const selectedName = Object.entries(masterData.trip_types).find(([name, id]) => {
                          const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                          return idValue === selectedId;
                        })?.[0] || '';
                        updateDay(index, { custom_trip_type_id: selectedId, custom_trip_type: selectedName });
                      }
                    }}
                    className="input-field"
                  >
                    <option value={-1}>SELECT</option>
                    {masterData.trip_types && typeof masterData.trip_types === 'object' && Object.entries(masterData.trip_types).map(([name, id]) => {
                      const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                      return idValue !== null ? <option key={idValue} value={idValue}>{name}</option> : null;
                    })}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Activities</label>
                <div className="space-y-2">
                  {day.activities?.map((activity, actIndex) => (
                    <div key={actIndex} className="flex gap-2 items-center">
                      <div className="flex-1">
                        <SearchableSelect
                          options={masterData.activities && typeof masterData.activities === 'object' ? Object.entries(masterData.activities).map(([name, id]) => {
                            const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                            return idValue !== null ? { value: idValue, label: name } : null;
                          }).filter((opt): opt is { value: number; label: string } => opt !== null) : []}
                          value={activity.activity_id}
                          onChange={(value) => {
                            const selectedOption = masterData.activities && typeof masterData.activities === 'object' ? 
                              Object.entries(masterData.activities).find(([name, id]) => {
                                const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                                return idValue === value;
                              }) : null;
                            
                            if (selectedOption) {
                              const updated = [...dayWiseDetails];
                              updated[index].activities[actIndex] = {
                                activity_id: value as number,
                                activity_name: selectedOption[0],
                                calculated_adults: activity.calculated_adults
                              };
                              onChange(updated);
                            }
                          }}
                          placeholder="Select activity..."
                          className="text-sm"
                        />
                      </div>
                      <button
                        onClick={() => {
                          const updated = [...dayWiseDetails];
                          updated[index].activities.splice(actIndex, 1);
                          onChange(updated);
                        }}
                        className="px-2 py-1 bg-red-100 text-red-600 rounded text-sm hover:bg-red-200"
                      >
                        ✕
                      </button>
                    </div>
                  )) || []}
                  <button
                    onClick={() => {
                      const updated = [...dayWiseDetails];
                      if (!updated[index].activities) updated[index].activities = [];
                      updated[index].activities.push({ activity_id: -1, activity_name: '', calculated_adults: 0 });
                      onChange(updated);
                    }}
                    className="px-3 py-1 bg-blue-100 text-blue-600 rounded text-sm hover:bg-blue-200"
                  >
                    + Add Activity
                  </button>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Tour Description</label>
                  <button
                    type="button"
                    onClick={() => updateDay(index, { auto_populate_description: !day.auto_populate_description })}
                    className={`flex items-center gap-2 px-3 py-1 rounded text-xs font-medium transition-colors ${
                      day.auto_populate_description
                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span>{day.auto_populate_description ? '🔄' : '✏️'}</span>
                    <span>Auto Populate</span>
                  </button>
                </div>
                <textarea
                  value={day.custom_tour_description || ''}
                  onChange={(e) => updateDay(index, { custom_tour_description: e.target.value })}
                  className="input-field"
                  rows={6}
                  placeholder={day.auto_populate_description ? "Description will auto-populate when you select activities..." : "Enter tour description manually..."}
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Tour</label>
              <SearchableSelect
                options={(() => {
                  const toursMap = masterData.tours_name_id || masterData.tours || {};
                  const options = Object.entries(toursMap).map(([name, id]) => {
                    const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                    return idValue !== null ? { value: idValue, label: name } : null;
                  }).filter((opt): opt is { value: number; label: string } => opt !== null);
                  
                  return options;
                })()}
                value={day.tour_id !== -1 ? day.tour_id : ''}
                onChange={(value) => {
                  if (value) {
                    const selectedOption = (() => {
                      const toursMap = masterData.tours_name_id || masterData.tours || {};
                      return Object.entries(toursMap).find(([name, id]) => {
                        const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                        return idValue === value;
                      });
                    })();
                    
                    if (selectedOption) {
                      updateDay(index, { tour_id: value as number, tour_name: selectedOption[0] });
                    }
                  } else {
                    updateDay(index, { tour_id: -1, tour_name: '' });
                  }
                }}
                placeholder="Select tour..."
                className="text-sm"
              />
              {(() => {
                const shouldShow = day.tour_id !== -1 && masterData.tours_details && masterData.tours_details[day.tour_id];
                return shouldShow ? (
                  <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded">
                    <p className="text-sm font-semibold mb-2">Tour Description:</p>
                    <div className="text-sm text-gray-700 space-y-1">
                      {masterData.tours_details![day.tour_id].split('\n').map((line: string, i: number) => {
                        const trimmed = line.trim();
                        if (trimmed.startsWith('-')) {
                          return <div key={i} className="flex gap-2"><span>•</span><span>{trimmed.substring(1).trim()}</span></div>;
                        }
                        return trimmed ? <div key={i}>{line}</div> : <div key={i} className="h-2"></div>;
                      })}
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {/* Meals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {day.lunch_required && (
              <div>
                <div className="flex gap-2 mb-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">Lunch</label>
                  </div>
                  <div className="w-20">
                    <div className="relative group">
                      <label className="block text-xs text-gray-600 cursor-help text-center">FOC</label>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                        FOC: Free of Cost (Usually 1 FOC per 15 paying guests)
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <select
                      value={day.lunch?.meal_id || -1}
                      onChange={(e) => {
                        const id = parseInt(e.target.value);
                        const name = Object.keys(masterData.meals).find(k => masterData.meals[k] === id) || '';
                        updateDay(index, { lunch: { meal_id: id, meal_name: name, foc: day.lunch?.foc || 0 } });
                      }}
                      className="input-field"
                    >
                      <option value={-1}>SELECT</option>
                      {masterData.meals && typeof masterData.meals === 'object' && Object.entries(masterData.meals).map(([name, id]) => {
                        const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                        return idValue !== null ? <option key={idValue} value={idValue}>{name}</option> : null;
                      })}
                    </select>
                  </div>
                  <div className="w-20">
                    <input
                      type="number"
                      min="0"
                      value={day.lunch?.foc || 0}
                      onChange={(e) => updateDay(index, { lunch: { ...day.lunch, meal_id: day.lunch?.meal_id || -1, meal_name: day.lunch?.meal_name || '', foc: parseInt(e.target.value) || 0 } })}
                      className="input-field"
                    />
                  </div>
                </div>
              </div>
            )}
            {day.dinner_required && (
              <div>
                <div className="flex gap-2 mb-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">Dinner</label>
                  </div>
                  <div className="w-20">
                    <div className="relative group">
                      <label className="block text-xs text-gray-600 cursor-help text-center">FOC</label>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                        FOC: Free of Cost (Usually 1 FOC per 15 paying guests)
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <select
                      value={day.dinner?.meal_id || -1}
                      onChange={(e) => {
                        const id = parseInt(e.target.value);
                        const name = Object.keys(masterData.meals).find(k => masterData.meals[k] === id) || '';
                        updateDay(index, { dinner: { meal_id: id, meal_name: name, foc: day.dinner?.foc || 0 } });
                      }}
                      className="input-field"
                    >
                      <option value={-1}>SELECT</option>
                      {masterData.meals && typeof masterData.meals === 'object' && Object.entries(masterData.meals).map(([name, id]) => {
                        const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                        return idValue !== null ? <option key={idValue} value={idValue}>{name}</option> : null;
                      })}
                    </select>
                  </div>
                  <div className="w-20">
                    <input
                      type="number"
                      min="0"
                      value={day.dinner?.foc || 0}
                      onChange={(e) => updateDay(index, { dinner: { ...day.dinner, meal_id: day.dinner?.meal_id || -1, meal_name: day.dinner?.meal_name || '', foc: parseInt(e.target.value) || 0 } })}
                      className="input-field"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Day Addons */}
          <div className="mt-4 border-t pt-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-semibold text-gray-900">Day Addons</h4>
              <div className="flex gap-2">
                <button onClick={() => addDayAddon(index)} className="px-3 py-1 bg-primary-100 text-primary-700 rounded text-sm hover:bg-primary-200">
                  ➕ Add
                </button>
                <button onClick={() => removeDayAddon(index)} className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300">
                  ➖ Remove
                </button>
              </div>
            </div>
            {day.day_addons.map((addon, addonIndex) => (
              <div key={addonIndex} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                <SearchableSelect
                  options={masterData.day_addon_activities && typeof masterData.day_addon_activities === 'object' ? Object.entries(masterData.day_addon_activities).map(([name, id]) => {
                    const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                    return idValue !== null ? { value: idValue, label: name } : null;
                  }).filter((opt): opt is { value: number; label: string } => opt !== null) : []}
                  value={addon.activity_id !== -1 ? addon.activity_id : ''}
                  onChange={(value) => {
                    if (value) {
                      const selectedOption = masterData.day_addon_activities && typeof masterData.day_addon_activities === 'object' ? 
                        Object.entries(masterData.day_addon_activities).find(([name, id]) => {
                          const idValue = typeof id === 'number' ? id : (typeof id === 'object' && id !== null && 'id' in id ? (id as any).id : null);
                          return idValue === value;
                        }) : null;
                      
                      if (selectedOption) {
                        updateDayAddon(index, addonIndex, 'activity_id', value);
                        updateDayAddon(index, addonIndex, 'activity_name', selectedOption[0]);
                      }
                    } else {
                      updateDayAddon(index, addonIndex, 'activity_id', -1);
                      updateDayAddon(index, addonIndex, 'activity_name', '');
                    }
                  }}
                  placeholder="Select addon..."
                  className="text-sm"
                />
                <select
                  value={addon.main_activity_id ?? 0}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '0') {
                      updateDayAddon(index, addonIndex, 'main_activity_id', 0);
                      updateDayAddon(index, addonIndex, 'main_activity_name', 'General');
                    } else {
                      const opt = getMainActivityOptions(day).find(o => o.id === parseInt(val));
                      if (opt) {
                        updateDayAddon(index, addonIndex, 'main_activity_id', opt.id);
                        updateDayAddon(index, addonIndex, 'main_activity_name', opt.name);
                      }
                    }
                  }}
                  className="input-field text-sm"
                >
                  <option value={0}>General</option>
                  {getMainActivityOptions(day).map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="1"
                  value={addon.calculated_adults}
                  onChange={(e) => updateDayAddon(index, addonIndex, 'calculated_adults', parseInt(e.target.value) || 0)}
                  className="input-field"
                  placeholder="Quantity"
                />
              </div>
            ))}
          </div>

          {/* Day Xtras */}
          <div className="mt-4 border-t pt-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-semibold text-gray-900">Day Xtras</h4>
              <div className="flex gap-2">
                <button onClick={() => addDayExtra(index)} className="px-3 py-1 bg-primary-100 text-primary-700 rounded text-sm hover:bg-primary-200">
                  ➕ Add
                </button>
                <button onClick={() => removeDayExtra(index)} className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300">
                  ➖ Remove
                </button>
              </div>
            </div>
            {day.day_extras.map((extra, extraIndex) => (
              <div key={extraIndex} className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-2">
                {extraIndex === 0 && (
                  <>
                    <div className="text-xs font-medium text-gray-600 mb-1">Extra Text</div>
                    <div className="text-xs font-medium text-gray-600 mb-1">Main Activity</div>
                    <div className="text-xs font-medium text-gray-600 mb-1">Rate</div>
                    <div className="text-xs font-medium text-gray-600 mb-1">Number</div>
                    <div className="text-xs font-medium text-gray-600 mb-1">Display</div>
                  </>
                )}
                <input
                  type="text"
                  value={extra.extra_text}
                  onChange={(e) => updateDayExtra(index, extraIndex, 'extra_text', e.target.value)}
                  className="input-field"
                  placeholder="Extra text"
                />
                <select
                  value={extra.main_activity_id ?? 0}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '0') {
                      updateDayExtra(index, extraIndex, 'main_activity_id', 0);
                      updateDayExtra(index, extraIndex, 'main_activity_name', 'General');
                    } else {
                      const opt = getMainActivityOptions(day).find(o => o.id === parseInt(val));
                      if (opt) {
                        updateDayExtra(index, extraIndex, 'main_activity_id', opt.id);
                        updateDayExtra(index, extraIndex, 'main_activity_name', opt.name);
                      }
                    }
                  }}
                  className="input-field text-sm"
                >
                  <option value={0}>General</option>
                  {getMainActivityOptions(day).map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={extra.rate}
                  onChange={(e) => updateDayExtra(index, extraIndex, 'rate', parseFloat(e.target.value) || 0)}
                  className="input-field"
                  placeholder="Rate"
                />
                <input
                  type="number"
                  min="1"
                  value={extra.number}
                  onChange={(e) => updateDayExtra(index, extraIndex, 'number', parseInt(e.target.value) || 0)}
                  className="input-field"
                  placeholder="Number"
                />
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={extra.display_in_itinerary}
                    onChange={(e) => updateDayExtra(index, extraIndex, 'display_in_itinerary', e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded mr-2"
                  />
                  <label className="text-sm text-gray-700">Display in itinerary</label>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DayWiseForm;
